// @flow
import * as React from 'react';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import type { ApiClient } from 'speed-js-core';
import {
    get, isEqual, set, unset,
} from 'lodash';
import memoizeOne from 'memoize-one';
import endsWith from 'lodash/endsWith';
import GrommetForm from '../../components/grommet2/forms/GrommetForm';

type Props = {
    // eslint-disable-next-line react/no-unused-prop-types
    resourceName: string,
    // eslint-disable-next-line
    resourceArguments?: Array<any>,
    // eslint-disable-next-line react/no-unused-prop-types
    additionalQuery?: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    excludeFields?: Array<string>,
    // eslint-disable-next-line react/no-unused-prop-types
    unsetPaths?: Array<string>,
    // eslint-disable-next-line react/no-unused-prop-types
    onFiltersSchemaLoad: () => void,
    onSubmit: (e: SyntheticEvent<HTMLFormElement>) => void,
    children?: (schema: Object) => React.Node,
    storageKey: string,
    customSchema: Object,
}


type CombinedProps = Props & {
    getSchema: () => Promise<Object>,
    schemaResult: Object,
    schemaInitialValues: Object,
};

const excludeSchemaFields = memoizeOne((schemaResult, excludeFields, unsetPaths) => {
    // Deep clone schemaResult in order to remove excluded fields from schema without modifying schemaResult
    const schema = JSON.parse(JSON.stringify(schemaResult));
    if (excludeFields) {
        schema.fields = schema.fields.filter((value) => excludeFields.indexOf(value[0]) === -1);
    }
    if (unsetPaths) {
        unsetPaths.forEach((path) => {
            const delimiterIndex = path.indexOf('.');
            const fieldName = path.substring(0, delimiterIndex);
            const pathName = path.substring(delimiterIndex + 1);
            const field = schema.fields.find((schemaField) => schemaField[0] === fieldName);
            if (field) {
                unset(field[1], pathName);
            }
        });
    }
    return schema;
});

const getInitialValues = memoizeOne((fields) => {
    const result = {};
    fields.forEach((schemaField) => {
        const [name, value] = schemaField;
        if (!value.read_only) {
            const { initial: fieldInitial } = value;
            if (typeof fieldInitial !== 'undefined' && fieldInitial !== '' && fieldInitial !== null) {
                set(result, name, fieldInitial);
            }
        }
    });
    return result;
});

@connectAPI((apiClient: ApiClient, props: Props) => ({
    getSchema: {
        send: () => {
            const args = [...props.resourceArguments, { ...props.additionalQuery }];
            if (props.customSchema) {
                return new Promise((res) => { res(props.customSchema); });
            }
            if (props.resourceMethod && props.resourceMethod !== 'list') {
                return get(apiClient.resources, props.resourceName).schemaMethod(...args);
            }

            return get(apiClient.resources, props.resourceName).schema(...args);
        },
        success: (schema: Object) => {
            const { onFiltersSchemaLoad, excludeFields, unsetPaths } = props;
            const initialValues = schema.filters ? getInitialValues(
                excludeSchemaFields(schema.filters, excludeFields, unsetPaths).fields,
            ) : {};
            if (onFiltersSchemaLoad) {
                onFiltersSchemaLoad(initialValues);
            }
            return { schemaResult: schema.filters, schemaInitialValues: initialValues };
        },
    },
}))
class FilterForm extends React.Component<CombinedProps> {
    static defaultProps: Object;

    componentDidMount() {
        const { getSchema, customSchema } = this.props;
        getSchema();
    }

    componentDidUpdate(prevProps: CombinedProps): void {
        const {
            resourceName, resourceArguments, additionalQuery, getSchema, customSchema,
        } = this.props;

        if (prevProps.resourceName !== resourceName
            || !isEqual(prevProps.resourceArguments, resourceArguments)
            || !isEqual(prevProps.additionalQuery, additionalQuery)
        ) {
            getSchema();
        }
    }

    render() {
        const {
            schemaResult, schemaInitialValues, onSubmit, children, storageKey, excludeFields,
            unsetPaths, customSchema,
        } = this.props;
        if (!schemaResult && !customSchema) {
            return null;
        }

        // Exclude fields and unset paths
        const schema = customSchema || excludeSchemaFields(schemaResult, excludeFields, unsetPaths);

        const filterSessionStorage = sessionStorage.getItem(storageKey);
        if (filterSessionStorage && filterSessionStorage !== '{}') {
            const filterSessionStorageJSON = JSON.parse(filterSessionStorage);
            schema.fields.forEach((schemaField) => {
                const [name, value] = schemaField;
                if (Object.keys(filterSessionStorageJSON).indexOf(name) === -1) {
                    // Filter field not in session storage, reset its value to null
                    schemaField[1].initial = null;
                }
            });
            Object.entries(filterSessionStorageJSON).forEach(([name, value]) => {
                const field = schema.fields.find((schemaField) => schemaField[0] === name);
                if (field) {
                    field[1].initial = value;
                // Handles date range picker initials
                }
            });
        }

        if (children) {
            return children({ schema, initialValues: schemaInitialValues, onSubmit });
        }

        return (
            // $FlowFixMe
            <GrommetForm
                schema={schema}
                onSubmit={onSubmit}
            />
        );
    }
}

FilterForm.defaultProps = {
    children: null,
    resourceArguments: [],
    additionalQuery: [],
    excludeFields: [],
    unsetPaths: [],
    getSchema: () => null,
};

export default FilterForm;
